import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Loader2, Save, Trash2 } from 'lucide-react'
import { DetailHeader, DetailPage } from '@/components/detail-page'
import { useAuth } from '@/contexts/AuthContext'
import { WorkspaceAsideCard, WorkspacePageGrid, WorkspaceSection } from '@/components/workspace-form'
import { Button } from '@/components/ui/button'
import { FormMessage } from '@/components/ui/form-message'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useConfirm } from '@/components/ui/use-confirm'
import { useToast } from '@/components/ui/use-toast'
import { apiClient } from '@/lib/api-client'
import type { Network } from '@/lib/api-client'

function SettingsToggleRow({
  id,
  label,
  helper,
  checked,
  onCheckedChange,
}: {
  id: string
  label: string
  helper: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-border/70 bg-background/60 px-4 py-4">
      <div className="space-y-1">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-sm leading-6 text-muted-foreground">{helper}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  )
}

export default function NetworkSettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { confirm } = useConfirm()
  const { toast } = useToast()
  const [network, setNetwork] = useState<Network | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ defaultBranch?: string }>({})
  const [settings, setSettings] = useState({
    autoValidate: false,
    defaultBranch: 'main',
    notifyOnFailure: true,
    notifyOnSuccess: false,
  })

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
    } else if (params.id) {
      fetchNetwork()
    }
  }, [isAuthenticated, params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNetwork = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getNetwork(params.id!)

      if (response.success && response.data) {
        setNetwork(response.data.network)
        setSettings({
          autoValidate: false,
          defaultBranch: response.data.network.defaultBranch || 'main',
          notifyOnFailure: true,
          notifyOnSuccess: false,
        })
      } else {
        setError(response.error || 'Failed to load network')
        if (response.error?.includes('404') || response.error?.includes('not found')) {
          navigate('/networks')
        }
      }
    } catch (err) {
      console.error('Failed to fetch network:', err)
      setError('Failed to load network')
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    if (!settings.defaultBranch.trim()) {
      setFieldErrors({
        defaultBranch: 'Enter the branch to use for validations.',
      })
      return
    }

    setSaving(true)
    try {
      const response = await apiClient.updateNetwork(params.id!, {
        defaultBranch: settings.defaultBranch.trim(),
      })

      if (response.success) {
        toast({
          title: 'Settings saved',
          description: 'Network defaults were updated successfully.',
          variant: 'success',
        })
        fetchNetwork()
      } else {
        throw new Error(response.error || 'Failed to save settings')
      }
    } catch (saveError) {
      console.error('Failed to save settings:', saveError)
      toast({
        title: 'Save failed',
        description: saveError instanceof Error ? saveError.message : 'Failed to save settings',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const deleteNetwork = async () => {
    const confirmed = await confirm({
      title: 'Remove network',
      description: 'This permanently deletes the network, settings, and validation history.',
      confirmLabel: 'Remove network',
      cancelLabel: 'Keep network',
      variant: 'destructive',
    })

    if (!confirmed) {
      return
    }

    try {
      const response = await apiClient.deleteNetwork(params.id!)

      if (response.success) {
        toast({
          title: 'Network removed',
          description: `Removed "${network?.name ?? 'network'}".`,
          variant: 'success',
        })
        navigate('/networks')
      } else {
        toast({
          title: 'Remove failed',
          description: response.error || 'Failed to delete network',
          variant: 'destructive',
        })
      }
    } catch (deleteError) {
      console.error('Failed to delete network:', deleteError)
      toast({
        title: 'Remove failed',
        description: 'Failed to delete network',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <DetailPage className="max-w-6xl">
        <WorkspaceAsideCard title="Loading settings" description="Retrieving network configuration now.">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Reading current network defaults.
          </div>
        </WorkspaceAsideCard>
      </DetailPage>
    )
  }

  if (error) {
    return (
      <DetailPage className="max-w-6xl">
        <WorkspaceAsideCard title="Settings unavailable" description={error} tone="danger">
          <div className="flex items-center gap-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Resolve the load error before editing network defaults.
          </div>
        </WorkspaceAsideCard>
      </DetailPage>
    )
  }

  if (!network) {
    return null
  }

  const repoName =
    'sourceConfig' in network && 'fullName' in network.sourceConfig
      ? String(network.sourceConfig.fullName)
      : null

  const sidebar = (
    <>
      <WorkspaceAsideCard title="Network" description={network.name}>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Source</dt>
            <dd className="font-medium capitalize text-foreground">
              {network.sourceType === 'cli' ? 'CLI upload' : network.sourceType}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Branch</dt>
            <dd className="font-medium text-foreground">{settings.defaultBranch || 'Unset'}</dd>
          </div>
        </dl>
      </WorkspaceAsideCard>
      <WorkspaceAsideCard
        title="Save behavior"
        description="Changes here stay limited to network defaults. Destructive removal is isolated in its own section and still uses the confirmation flow."
      />
    </>
  )

  return (
    <DetailPage className="max-w-6xl">
      <DetailHeader
        title="Network settings"
        description={`Adjust source defaults, validation behavior, and notifications for ${network.name}.`}
        breadcrumbs={(
          <>
            <button type="button" className="text-left transition-colors hover:text-foreground" onClick={() => navigate('/networks')}>
              Networks
            </button>
            <span>/</span>
            <button
              type="button"
              className="text-left transition-colors hover:text-foreground"
              onClick={() => navigate(`/networks/${params.id}`)}
            >
              {network.name}
            </button>
            <span>/</span>
            <span className="font-medium text-foreground">Settings</span>
          </>
        )}
        actions={(
          <Button variant="ghost" onClick={() => navigate(`/networks/${params.id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Network
          </Button>
        )}
      />

      <WorkspacePageGrid
        aside={sidebar}
        main={(
          <>
            <WorkspaceSection
              title="Source and defaults"
              description="Keep source metadata readable, then edit only the fields operators are expected to change during routine maintenance."
              actions={(
                <Button onClick={saveSettings} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {saving ? 'Saving...' : 'Save settings'}
                </Button>
              )}
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Source type</Label>
                  <Input
                    value={network.sourceType === 'cli' ? 'CLI upload' : network.sourceType}
                    disabled
                    className="bg-muted/40 capitalize"
                  />
                  <FormMessage>The source type is fixed at creation time so each workspace has one clear provenance path.</FormMessage>
                </div>

                <div className="space-y-2">
                  <Label>Repository</Label>
                  <Input
                    value={repoName ?? 'No connected repository'}
                    disabled
                    className="bg-muted/40"
                  />
                  <FormMessage>
                    {repoName
                      ? 'Repository linkage is shown here for reference. Branch changes below affect validation defaults only.'
                      : 'This network currently relies on CLI uploads rather than a connected repository.'}
                  </FormMessage>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-branch">Default branch</Label>
                <Input
                  id="default-branch"
                  value={settings.defaultBranch}
                  onChange={(event) => {
                    setSettings((previous) => ({ ...previous, defaultBranch: event.target.value }))
                    if (fieldErrors.defaultBranch) {
                      setFieldErrors({ defaultBranch: undefined })
                    }
                  }}
                  aria-invalid={Boolean(fieldErrors.defaultBranch)}
                  placeholder="main"
                />
                {fieldErrors.defaultBranch ? (
                  <FormMessage variant="error">{fieldErrors.defaultBranch}</FormMessage>
                ) : (
                  <FormMessage>The branch used when a validation or sync run needs a default source revision.</FormMessage>
                )}
              </div>

              {network.sourceType === 'cli' ? (
                <WorkspaceAsideCard
                  title="CLI-managed network"
                  description="This workspace has no repository source attached. Continue using the CLI flow to upload configuration, and use this page only for calm reference and branch defaults."
                />
              ) : null}
            </WorkspaceSection>

            <WorkspaceSection
              title="Validation behavior"
              description="Operational toggles stay grouped together so the effect of each choice is easy to scan."
            >
              {network.sourceType === 'github' ? (
                <SettingsToggleRow
                  id="auto-validate"
                  label="Automatic validation"
                  helper="Run validation automatically when supported GitHub events arrive for the selected repository."
                  checked={settings.autoValidate}
                  onCheckedChange={(checked) => setSettings((previous) => ({ ...previous, autoValidate: checked }))}
                />
              ) : (
                <WorkspaceAsideCard
                  title="Automatic validation unavailable"
                  description="Automatic validation is only available for repository-backed networks. CLI-managed networks keep execution controls outside the app."
                />
              )}
            </WorkspaceSection>

            <WorkspaceSection
              title="Notifications"
              description="These controls are presented together so success and failure messaging reads like one system."
            >
              <div className="space-y-4">
                <SettingsToggleRow
                  id="notify-failure"
                  label="Notify on failure"
                  helper="Send a notification when a validation finishes in a failed state."
                  checked={settings.notifyOnFailure}
                  onCheckedChange={(checked) => setSettings((previous) => ({ ...previous, notifyOnFailure: checked }))}
                />
                <SettingsToggleRow
                  id="notify-success"
                  label="Notify on success"
                  helper="Send a notification after a successful validation when the team needs explicit completion signals."
                  checked={settings.notifyOnSuccess}
                  onCheckedChange={(checked) => setSettings((previous) => ({ ...previous, notifyOnSuccess: checked }))}
                />
              </div>
            </WorkspaceSection>

            <WorkspaceSection
              title="Danger zone"
              description="Deletion remains isolated from routine settings so destructive intent is explicit."
              tone="danger"
            >
              <WorkspaceAsideCard
                title="Remove this network"
                description="Deleting a network removes its settings and validation history. This cannot be undone."
                tone="danger"
              >
                <Button variant="destructive" onClick={deleteNetwork}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove network
                </Button>
              </WorkspaceAsideCard>
            </WorkspaceSection>
          </>
        )}
      />
    </DetailPage>
  )
}
